import { memo } from "react";

import { useChartsContext } from "contexts/ChartContext";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

/**
 * The dashed "this chart is not on the global focus" frame the wells charts show
 * (product: "chart focus should have the dotted line around the
 * widget like in well charts").
 *
 * Deliberately NOT the shared ChartSettingsVsGlobalSettings, even though the
 * styles are copied from it verbatim so the two frames look identical. That
 * component decides via useChartVsGlobalSettings, which also compares time step,
 * production source and normalization against the global settings — none of
 * which this widget has (they are `show: false` feature entities holding stock
 * defaults). A user whose global source is Private would therefore see this
 * widget framed for a setting it does not expose, and the same hook force-opens
 * the legend whenever chart focus turns on, which is the behaviour QA already
 * asked us to stop.
 *
 * So: chart focus, and nothing else.
 */
function WellStatusChartFocusBorder() {
  const { id } = useChartsContext();
  const { chartFocus, screenshot } = useChartEntities(id);

  // A dashed frame around the capture is chrome, not data.
  if (!chartFocus?.active || screenshot?.active) return null;

  return (
    <FocusFrame data-testid="well-status-chart-focus-border">
      <FocusMessage data-testid="well-status-chart-focus-message">
        Chart specific settings
      </FocusMessage>
    </FocusFrame>
  );
}

export default memo(WellStatusChartFocusBorder);

// Both styles mirror ChartSettingsVsGlobalSettings so the frame and its pill are
// pixel-identical to the wells charts'.
const FocusFrame = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  border: 2px dashed var(--color-primary);
  pointer-events: none;
`;

const FocusMessage = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  color: #fff;
  background-color: var(--color-primary);
  padding: 2px 5px;
  border-top-right-radius: 5px;
  pointer-events: none;
  opacity: 1;
  animation: fadeOut 1s ease-in-out 4s forwards;
`;
